import { MEMBERSHIP_STAGES } from '@dosfilos/domain';
import type {
  CapCertification,
  CapNetworkSubmission,
  MembershipStage,
  NetworkAffiliation,
  NetworkSubmission,
  NetworkSubmissionStatus,
} from '@dosfilos/domain';

// ─── Lo que el revisor de TMAI tiene que ver al entrar ──────────────────────
//
// La bandeja anterior listaba tres bloques y dejaba fuera lo que más pesa: una
// certificación otorgada no aparecía en ninguna pantalla de la red, y la fila de
// una propuesta CAP —la única que certifica un programa— era la única sin
// estado, sin antigüedad y sin botón para abrirla.
//
// Acá vive la parte que se puede probar sin pintar nada: qué espera decisión,
// cuánto lleva esperando y qué pasó antes. Membresía y CAP entran por caminos
// distintos y salen con la misma forma, porque quien revisa decide sobre las dos
// en la misma sesión.

export type QueueSubject = 'membership' | 'cap';
export type QueueKind = 'formal' | 'advisory' | 'legacy';

export interface QueueItem {
  id: string;
  subject: QueueSubject;
  kind: QueueKind;
  status: NetworkSubmissionStatus;
  /** Qué se decide: el programa (CAP) o el escalón que se pide (membresía). */
  title: string;
  seminaryTenantId: string;
  seminaryName: string;
  tmaiSiteAbbreviation?: string;
  round: number;
  submittedAt: string;
  decidedAt?: string;
  /** Sólo CAP: para enlazar la ficha y mostrar CAP + nivel. */
  capCode?: CapNetworkSubmission['capCode'];
  level?: CapNetworkSubmission['level'];
  /** Sólo membresía: el escalón que la ronda pide. */
  stage?: MembershipStage;
  /** Ruta de la pantalla de decisión. */
  href: string;
}

const dayMs = 24 * 60 * 60 * 1000;

/** Días enteros desde que se presentó. Se calcula con la fecha que se le pasa —
 *  nunca con `Date.now()` adentro— para que el cálculo sea probable y para que
 *  una lista entera se mida contra el mismo instante. */
export function daysWaiting(item: QueueItem, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(item.submittedAt).getTime()) / dayMs));
}

/** Una ronda pasada del umbral que la RED declaró para su tipo. Sin umbral no
 *  hay atraso: inventar un número acá afirmaría una política que TMAI no fijó. */
export function isOverdue(
  item: QueueItem,
  now: Date,
  noticeDays: { formal?: number; advisory?: number } | undefined,
): boolean {
  const threshold = item.kind === 'advisory' ? noticeDays?.advisory : noticeDays?.formal;
  return threshold !== undefined && daysWaiting(item, now) >= threshold;
}

export function membershipToQueueItem(s: NetworkSubmission): QueueItem {
  return {
    id: s.id,
    subject: 'membership',
    kind: s.submissionKind === 'legacy' ? 'legacy' : s.submissionKind,
    status: s.status,
    title: s.stage,
    seminaryTenantId: s.seminaryTenantId,
    seminaryName: s.seminaryName,
    ...(s.tmaiSiteAbbreviation ? { tmaiSiteAbbreviation: s.tmaiSiteAbbreviation } : {}),
    round: s.round,
    submittedAt: s.submittedAt,
    ...(s.decision?.decidedAt ? { decidedAt: s.decision.decidedAt } : {}),
    stage: s.stage,
    href: `/red/postulaciones/${s.id}`,
  };
}

export function capToQueueItem(s: CapNetworkSubmission): QueueItem {
  return {
    id: s.id,
    subject: 'cap',
    kind: s.submissionKind,
    status: s.status,
    title: s.programName,
    seminaryTenantId: s.seminaryTenantId,
    seminaryName: s.seminaryName,
    ...(s.tmaiSiteAbbreviation ? { tmaiSiteAbbreviation: s.tmaiSiteAbbreviation } : {}),
    round: s.round,
    submittedAt: s.submittedAt,
    ...(s.decision?.decidedAt ? { decidedAt: s.decision.decidedAt } : {}),
    capCode: s.capCode,
    level: s.level,
    href: `/red/propuestas/${s.id}`,
  };
}

/** Abierta = espera decisión. Es el único estado que obliga a alguien. */
export const isOpen = (i: QueueItem): boolean => i.status === 'submitted';

/** Lo que espera decisión, lo que más lleva esperando primero: el orden en que
 *  conviene atenderlas, no el orden en que llegaron. */
export function openQueue(items: QueueItem[]): QueueItem[] {
  return items
    .filter(isOpen)
    .sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
}

/** Lo ya decidido, lo más reciente primero. Incluye las rondas anteriores al
 *  modelo (`legacy`): para quien revisa hoy, todas son pasado. */
export function decidedQueue(items: QueueItem[]): QueueItem[] {
  return items
    .filter((i) => !isOpen(i))
    .sort(
      (a, b) =>
        new Date(b.decidedAt ?? b.submittedAt).getTime() -
        new Date(a.decidedAt ?? a.submittedAt).getTime(),
    );
}

export interface QueueFilter {
  subject?: QueueSubject | 'all';
  kind?: QueueKind | 'all';
  seminaryTenantId?: string | 'all';
}

export function filterQueue(items: QueueItem[], filter: QueueFilter): QueueItem[] {
  return items.filter(
    (i) =>
      (!filter.subject || filter.subject === 'all' || i.subject === filter.subject) &&
      (!filter.kind || filter.kind === 'all' || i.kind === filter.kind) &&
      (!filter.seminaryTenantId ||
        filter.seminaryTenantId === 'all' ||
        i.seminaryTenantId === filter.seminaryTenantId),
  );
}

export interface NetworkSummary {
  pending: number;
  pendingFormal: number;
  pendingAdvisory: number;
  overdue: number;
  activeCenters: number;
  centersByStage: Partial<Record<MembershipStage, number>>;
  certificationsGranted: number;
  certificationsRevoked: number;
  /** Vigentes con algo pendiente. */
  certificationsConditional: number;
  /** De esas, las que ya pasaron su fecha de revisión. */
  conditionsDue: number;
}

/** Las certificaciones cuya fecha de revisión ya llegó y que nadie resolvió.
 *
 *  Es lo que hace REAL la promesa de «aprobado con condiciones». Una condición
 *  que no vuelve a aparecer es una nota al pie: el centro la olvida, TMAI
 *  también, y la certificación queda como si nunca hubiera tenido reservas.
 *
 *  Avisa, nunca suspende (decisión del director, 2026-09-21): la certificación
 *  sigue vigente y es una persona la que decide qué hacer. Por eso esto es una
 *  lista que envejece a la vista, no un proceso automático. */
export function conditionsDue<T extends Pick<CapCertification, 'status' | 'conditions'>>(
  certifications: readonly T[],
  now: Date,
): T[] {
  const today = now.toISOString();
  return certifications.filter(
    (c) =>
      c.status === 'granted' &&
      !!c.conditions &&
      !c.conditions.resolvedAt &&
      c.conditions.reviewBy <= today,
  );
}

export function summarize(input: {
  items: QueueItem[];
  affiliations: NetworkAffiliation[];
  certifications: Pick<CapCertification, 'status' | 'conditions'>[];
  noticeDays: { formal?: number; advisory?: number } | undefined;
  now: Date;
}): NetworkSummary {
  const open = input.items.filter(isOpen);
  const active = input.affiliations.filter((a) => a.status === 'active');
  const centersByStage: Partial<Record<MembershipStage, number>> = {};
  for (const a of active)
    centersByStage[a.achievedStage] = (centersByStage[a.achievedStage] ?? 0) + 1;
  return {
    pending: open.length,
    pendingFormal: open.filter((i) => i.kind === 'formal').length,
    // `legacy` no se cuenta como consulta: no es ni una cosa ni la otra.
    pendingAdvisory: open.filter((i) => i.kind === 'advisory').length,
    overdue: open.filter((i) => isOverdue(i, input.now, input.noticeDays)).length,
    activeCenters: active.length,
    centersByStage,
    certificationsGranted: input.certifications.filter((c) => c.status === 'granted').length,
    certificationsRevoked: input.certifications.filter((c) => c.status === 'revoked').length,
    /** Vigentes pero con algo pendiente. Se cuentan aparte de las limpias: son
     *  certificaciones reales, no a medias, y a la vez no son lo mismo. */
    certificationsConditional: input.certifications.filter(
      (c) => c.status === 'granted' && !!c.conditions && !c.conditions.resolvedAt,
    ).length,
    conditionsDue: conditionsDue(input.certifications, input.now).length,
  };
}

export interface CenterCard {
  seminaryTenantId: string;
  seminaryName: string;
  tmaiSiteAbbreviation?: string;
  stage: MembershipStage;
  affiliationStatus: NetworkAffiliation['status'];
  openRounds: number;
  certifications: number;
}

/** El nombre del centro vive en las rondas, no en la afiliación: la afiliación
 *  es del lado de la red y solo guarda el id. Un centro afiliado que nunca
 *  presentó nada aparece igual, con su id como nombre — existir en la red es el
 *  dato, y esconderlo lo volvería invisible para quien lo afilió. */
export function centerCards(input: {
  affiliations: NetworkAffiliation[];
  items: QueueItem[];
  certifications: Pick<CapCertification, 'seminaryTenantId' | 'status'>[];
}): CenterCard[] {
  const nameById = new Map<string, { name: string; abbr?: string }>();
  for (const i of input.items) {
    nameById.set(i.seminaryTenantId, {
      name: i.seminaryName,
      ...(i.tmaiSiteAbbreviation ? { abbr: i.tmaiSiteAbbreviation } : {}),
    });
  }
  return input.affiliations
    .map((a) => {
      const known = nameById.get(a.seminaryTenantId);
      return {
        seminaryTenantId: a.seminaryTenantId,
        seminaryName: known?.name ?? a.seminaryTenantId,
        ...(known?.abbr ? { tmaiSiteAbbreviation: known.abbr } : {}),
        stage: a.achievedStage,
        affiliationStatus: a.status,
        openRounds: input.items.filter(
          (i) => i.seminaryTenantId === a.seminaryTenantId && isOpen(i),
        ).length,
        certifications: input.certifications.filter(
          (c) => c.seminaryTenantId === a.seminaryTenantId && c.status === 'granted',
        ).length,
      };
    })
    .sort((a, b) => b.openRounds - a.openRounds || a.seminaryName.localeCompare(b.seminaryName));
}

/** Escalera de membresía en orden, DERIVADA de la del dominio: escribir los
 *  peldaños otra vez acá es la forma de que un escalón nuevo exista en un lado y
 *  no en el otro. `notStarted` no es un peldaño, es no haber empezado. */
export const MEMBERSHIP_LADDER: MembershipStage[] = MEMBERSHIP_STAGES.filter(
  (s) => s !== 'notStarted',
);

export function ladderProgress(stage: MembershipStage): number {
  const i = MEMBERSHIP_LADDER.indexOf(stage);
  // `notStarted` y cualquier valor futuro no alcanzado: ningún peldaño encendido.
  return i < 0 ? 0 : i + 1;
}
